/* eslint react-hooks/rules-of-hooks: "warn" */
import React, { type FunctionComponent, Fragment } from 'react'
import { useParams } from 'react-router'
import { type ApolloError } from 'apollo-client'
import { css } from '@emotion/react'
import { spacing } from '@retailer-platform/shared-components'
import { HeroBannerDetailsContentText } from 'domains/merchandising/components/hero-banner-details/components/HeroBannerDetailsContentText'
import { HeroBannerBreadcrumbs } from 'domains/merchandising/components/hero-banner-breadcrumbs/HeroBannerBreadcrumbs'
import { HeroBannerDetailsSecondaryNav } from 'domains/merchandising/components/hero-banner-details/components/HeroBannerDetailsSecondaryNav'
import { HeroBannerDetailsDetail } from 'domains/merchandising/components/hero-banner-details/components/HeroBannerDetailsDetail'
import { HeroBannerDetailsLocation } from 'domains/merchandising/components/hero-banner-details/components/HeroBannerDetailsLocation'
import { HeroBannerDetailsLinkDestination } from 'domains/merchandising/components/hero-banner-details/components/HeroBannerDetailsLinkDestination'
import { HeroBannerDetailsContentImage } from 'domains/merchandising/components/hero-banner-details/components/HeroBannerDetailsContentImage'
import { HeroBannerDetailsPreview } from 'domains/merchandising/components/hero-banner-details/components/HeroBannerDetailsPreview'
import { type DashboardQueryResult } from 'domains/merchandising/api/hero-banners/getContentHeroBannerMesh.hooks'
import { useQueryHeroBanner } from 'domains/merchandising/utils/useQueryHeroBanner.hooks'
import { type ContentManagementConfigurableButton } from 'domains/merchandising/api'
import { useQueryHeroBannerMesh } from 'domains/merchandising/utils/useQueryHeroBannerMesh.hooks'
import { type HeroBannerQueryParams } from 'domains/merchandising/types/heroBanner.types'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import useAccessControl from 'legacy/components/AccessControl/useAccessControl'
import { Flipper } from 'legacy/contexts/flipper/FlipperContext'

const cardsContainer = css({
  margin: '0 auto',
  maxWidth: '960px',
  paddingTop: spacing.X32,
  paddingRight: spacing.X20,
  paddingBottom: spacing.X32,
  paddingLeft: spacing.X20,
  width: '100%',
})

export const HeroBannerDetails: FunctionComponent = () => {
  const { heroBannerId } = useParams<HeroBannerQueryParams>()

  const hasAccess = useAccessControl()
  const useMeshQuery = hasAccess({
    flippers: Flipper.MeshGetStorefrontPlacements,
  })

  let apiError: ApolloError, apiLoading: boolean, apiResult: DashboardQueryResult
  if (useMeshQuery) {
    ;({ apiError, apiLoading, apiResult } = useQueryHeroBannerMesh({ id: heroBannerId }))
  } else {
    ;({ apiError, apiLoading, apiResult } = useQueryHeroBanner({ id: heroBannerId }))
  }

  if (apiError) return <ErrorPage action={() => null} />
  if (apiLoading) return <LoadingPage />

  const placementContentType = apiResult.type

  const banners =
    apiResult.banners && apiResult.banners.length > 0
      ? apiResult.banners.map(banner => ({ ...apiResult, ...banner }))
      : [{ ...apiResult, locale: undefined }]

  return (
    <Fragment>
      <HeroBannerBreadcrumbs pageType="details" placementName={apiResult.name} />
      <HeroBannerDetailsSecondaryNav placementName={apiResult.name} />
      <div css={cardsContainer}>
        <HeroBannerDetailsDetail
          scheduling={apiResult.scheduling}
          name={apiResult.name}
          status={apiResult.status}
        />
        <HeroBannerDetailsLocation placementLocation={apiResult.platform} />
        <HeroBannerDetailsLinkDestination destination={apiResult.destination} />
        {banners.map(({ locale, ...apiResult }) => {
          const titleSuffix = locale ? ` (${locale})` : undefined
          return (
            <Fragment key={locale}>
              {/* TODO - these should be updated to be lowercase */}
              {placementContentType === 'IMAGE' && (
                <HeroBannerDetailsContentImage
                  titleSuffix={titleSuffix}
                  largeUrl={apiResult?.images?.large}
                  smallUrl={apiResult?.images?.small}
                  imageAltText={apiResult.imageAltText}
                />
              )}
              {placementContentType === 'TEXT' && (
                <HeroBannerDetailsContentText
                  titleSuffix={titleSuffix}
                  button={apiResult.button as ContentManagementConfigurableButton}
                  images={apiResult.images}
                  imageAltText={apiResult.imageAltText}
                  descriptionText={apiResult.descriptionText}
                  headingText={apiResult.headingText}
                  subheadingText={apiResult.subheadingText}
                  backgroundColor={apiResult.backgroundColor}
                />
              )}
              <HeroBannerDetailsPreview
                titleSuffix={titleSuffix}
                button={apiResult.button as ContentManagementConfigurableButton}
                images={apiResult.images}
                imageAltText={apiResult.imageAltText}
                descriptionText={apiResult.descriptionText}
                headingText={apiResult.headingText}
                subheadingText={apiResult.subheadingText}
                backgroundColor={apiResult.backgroundColor}
                destination={apiResult.destination}
                type={apiResult.type}
              />
            </Fragment>
          )
        })}
      </div>
    </Fragment>
  )
}
